import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Heart, ShoppingBag, RotateCcw, Truck, Shield, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductRail from '../components/ProductRail';
import ReviewsSection from '../components/ReviewsSection';
import CareInstructions from '../components/CareInstructions';
import { useStore } from '../context/StoreContext';
import { useLocale } from '../context/LocaleContext';
import { Button } from '../components/ui/button';
import { fetchProduct, fetchProducts } from '../api/client';
import { absMediaUrl } from '../components/ProductCard';
import { sortSizes } from '../lib/sizes';

const VatHint = () => {
  const { currency, t, vatRate } = useLocale();
  if (currency !== 'EUR' || !vatRate) return null;
  return <p className="text-[11px] text-green-700 font-semibold mt-1">✓ {t('vatIncluded', { rate: vatRate })}</p>;
};

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [size, setSize] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [pincode, setPincode] = useState('');
  const [pinMsg, setPinMsg] = useState('');
  const [showSizeErr, setShowSizeErr] = useState(false);
  const { addToCart, toggleWishlist, isWishlisted } = useStore();
  const { t, formatPrice, productPrice, productDiscount, deliveryDays } = useLocale();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const p = await fetchProduct(Number(id));
        if (cancelled) return;
        setProduct(p);
        setImgIdx(0);
        setSize(null);
        setShowSizeErr(false);
        // Fetch similar (same type)
        const sim = await fetchProducts({ category: p.category, limit: 12 });
        if (!cancelled) setSimilar((sim.items || []).filter((x) => x.id !== p.id).slice(0, 10));
      } catch (err) {
        console.warn('[PDP] fetch failed:', err.message);
        setProduct(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Inject Product structured data (rich results: price, availability, rating)
  useEffect(() => {
    if (!product) return;
    const ld = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      image: (Array.isArray(product.images) && product.images.length ? product.images : [product.image]).filter(Boolean),
      description: product.description || `${product.name} — premium streetwear from Feron Store.`,
      sku: String(product.id),
      brand: { '@type': 'Brand', name: product.collection || 'Feron Store' },
      offers: {
        '@type': 'Offer',
        priceCurrency: 'INR',
        price: String(product.price ?? ''),
        availability: product.inStock === false ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock',
        url: typeof window !== 'undefined' ? window.location.href : undefined,
      },
    };
    if (product.reviews > 0 && product.rating) {
      ld.aggregateRating = { '@type': 'AggregateRating', ratingValue: String(product.rating), reviewCount: String(product.reviews) };
    }
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = 'product-jsonld';
    el.text = JSON.stringify(ld);
    document.getElementById('product-jsonld')?.remove();
    document.head.appendChild(el);
    return () => { document.getElementById('product-jsonld')?.remove(); };
  }, [product]);

  const handleAdd = () => {
    if (!size) { setShowSizeErr(true); return; }
    setShowSizeErr(false);
    addToCart(product, size);
  };

  const checkPin = () => {
    if (pincode.length >= 4) setPinMsg(t('deliveryOk', { pin: pincode, days: deliveryDays }));
    else setPinMsg(t('invalidPin'));
  };

  if (loading) return (
    <div className="min-h-screen bg-white"><Header /><div className="max-w-4xl mx-auto p-10 text-center text-gray-500">Loading...</div><Footer /></div>
  );

  if (!product) return (
    <div className="min-h-screen bg-white"><Header />
      <div className="max-w-4xl mx-auto p-10 text-center">{t('productNotFound')} <Link to="/" className="text-[#22c55e] font-semibold">{t('goHome')}</Link></div>
      <Footer />
    </div>
  );

  const wl = isWishlisted(product.id);

  // Build a unified media gallery: videos first (auto-play on select), then images
  const videos = Array.isArray(product.videos) ? product.videos.filter(Boolean).map((u) => ({ kind: 'video', url: u })) : [];
  const imageList = Array.isArray(product.images) && product.images.length ? product.images : (product.image ? [product.image] : []);
  const images = imageList.filter(Boolean).map((u) => ({ kind: 'image', url: u }));
  const media = [...videos, ...images];
  const current = media[imgIdx] || { kind: 'image', url: product.image || '' };
  const sizes = sortSizes(Array.isArray(product.sizes) ? product.sizes : []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        <div className="text-xs text-gray-500 mb-4">
          <Link to="/" className="hover:text-[#22c55e]">{t('home')}</Link> / <Link to={`/shop/${product.gender}`} className="hover:text-[#22c55e]">{t(product.gender)}</Link> / <span>{product.type}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <div className="relative aspect-[3/4] bg-gray-100 overflow-hidden">
              {current.kind === 'video' ? (
                <video
                  key={current.url}
                  src={absMediaUrl(current.url)}
                  className="w-full h-full object-cover"
                  autoPlay muted loop playsInline controls
                />
              ) : (
                <img src={absMediaUrl(current.url)} alt={product.name} className="w-full h-full object-cover" />
              )}
              <button onClick={() => setImgIdx((imgIdx - 1 + media.length) % media.length)} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center hover:bg-white shadow"><ChevronLeft size={18} /></button>
              <button onClick={() => setImgIdx((imgIdx + 1) % media.length)} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center hover:bg-white shadow"><ChevronRight size={18} /></button>
            </div>
            <div className="flex gap-2 mt-3 overflow-x-auto">
              {media.map((m, i) => (
                <button key={`${m.url}-${i}`} onClick={() => setImgIdx(i)} className={`w-20 aspect-[3/4] overflow-hidden border-2 shrink-0 relative ${i === imgIdx ? 'border-[#22c55e]' : 'border-transparent'}`}>
                  {m.kind === 'video' ? (
                    <>
                      <video src={absMediaUrl(m.url)} muted playsInline className="w-full h-full object-cover" />
                      <span className="absolute inset-0 flex items-center justify-center bg-black/30 text-white text-lg">▶</span>
                    </>
                  ) : (
                    <img src={absMediaUrl(m.url)} alt="" className="w-full h-full object-cover" />
                  )}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-sm text-gray-500 uppercase tracking-wider">{product.collection}</div>
            <h1 className="text-2xl md:text-3xl font-black mt-1">{product.name}</h1>
            <div className="flex items-center gap-2 mt-2">
              <div className="flex items-center gap-1 bg-green-600 text-white px-2 py-0.5 rounded text-xs font-semibold">{product.rating} <Star size={12} className="fill-white" /></div>
              <span className="text-sm text-gray-500">({product.reviews} {t('reviews')})</span>
            </div>
            <div className="flex items-center gap-3 mt-4">
              <span className="text-3xl font-black">{productPrice(product, 'price')}</span>
              {(() => { const disc = productDiscount(product); return disc > 0 && <>
                <span className="text-lg text-gray-400 line-through">{productPrice(product, 'mrp')}</span>
                <span className="text-lg text-[#22c55e] font-semibold" data-testid="pdp-discount-badge">({disc}% {t('off')})</span>
              </>; })()}
            </div>
            <p className="text-xs text-gray-500 mt-1">{t('inclTaxes')}</p>
            <VatHint />

            <div className="mt-6">
              <div className="flex items-center justify-between">
                <h4 className="font-bold text-sm">{t('selectSize')}</h4>
              </div>
              <div className="flex gap-2 flex-wrap mt-3">
                {sizes.length === 0 ? (
                  <p className="text-sm text-gray-500">Sizes unavailable — please contact support.</p>
                ) : sizes.map((s) => (
                  <button key={s} data-testid={`size-btn-${s}`} onClick={() => { setSize(s); setShowSizeErr(false); }} className={`min-w-12 h-12 px-3 rounded-full border-2 text-sm font-semibold transition ${size === s ? 'border-[#22c55e] bg-[#22c55e] text-white' : 'border-gray-300 hover:border-black'}`}>{s}</button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button onClick={handleAdd} className="flex-1 h-14 bg-[#22c55e] hover:bg-[#16a34a] text-white font-bold tracking-wider text-base">
                <ShoppingBag className="mr-2" size={18} /> {t('addToBag')}
              </Button>
              <Button onClick={() => toggleWishlist(product)} variant="outline" className="h-14 px-6 border-2 border-black font-bold tracking-wider">
                <Heart className={`mr-2 ${wl ? 'fill-[#22c55e] text-[#22c55e]' : ''}`} size={18} /> {t('wishlistBtn')}
              </Button>
            </div>
            {showSizeErr && <p className="text-xs text-[#22c55e] mt-2">{t('pleaseSelectSize')}</p>}

            <div className="mt-6 border-t pt-6">
              <h4 className="font-bold text-sm mb-3">{t('checkDelivery')}</h4>
              <div className="flex gap-2">
                <input value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder={t('enterPincode')} className="border rounded px-3 py-2 text-sm flex-1 outline-none focus:border-[#22c55e]" />
                <button onClick={checkPin} className="text-sm font-bold text-[#22c55e] tracking-wider">{t('checkBtn')}</button>
              </div>
              {pinMsg && <p className="text-xs text-gray-600 mt-2">{pinMsg}</p>}
            </div>

            <CareInstructions items={product.careInstructions} />

            <div className="grid grid-cols-3 gap-3 mt-6 border-t border-b py-4">
              <div className="flex flex-col items-center text-center"><Truck className="text-[#22c55e]" size={22} /><span className="text-[11px] font-semibold mt-1">{t('freeShipping')}</span></div>
              <div className="flex flex-col items-center text-center"><RotateCcw className="text-[#22c55e]" size={22} /><span className="text-[11px] font-semibold mt-1">{t('dayReturns')}</span></div>
              <div className="flex flex-col items-center text-center"><Shield className="text-[#22c55e]" size={22} /><span className="text-[11px] font-semibold mt-1">{t('securePayments')}</span></div>
            </div>

            <div className="mt-6">
              <h4 className="font-bold text-sm mb-2">{t('productDetails')}</h4>
              <p className="text-sm text-gray-700 leading-relaxed">{product.description}</p>
              <ul className="text-sm text-gray-700 mt-3 list-disc pl-5 space-y-1">
                <li>{t('fabric')}</li>
                <li>{t('fit')}</li>
                <li>{t('pattern')}</li>
                <li>{t('washCare')}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <ReviewsSection productId={product.id} />

      {similar.length > 0 && <ProductRail title={t('youMayLike')} products={similar} />}
      <Footer />
    </div>
  );
};

export default ProductDetail;
